import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Select } from 'antd';
import moment from 'moment';

const { Option } = Select;

const CustomTimePicker = ({
    value = null,
    onChange = () => {},
    disabledHours: externallyDisabledHours = false,
    disabledMinutes: externallyDisabledMinutes = false,
    hourLabel = 'Hour',
    minuteLabel = 'Minute',
    ampmLabel = 'AM/PM',
    style = {},
    selectedDate = null,
    defaultOpenValue = null
}) => {
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 576 : false);
    const [isTablet, setIsTablet] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 576);
            setIsTablet(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        handleResize(); // Initial check
        return () => window.removeEventListener('resize', handleResize);
    }, []);
        

    const timeParts = useMemo(() => {
        if (value && typeof value === 'string') {
            const [h, m, p] = value.split(':');
            return [h || null, m || null, p || null];
        }
        return [null, null, null];
    }, [value]);
    const [hour, minute, period] = timeParts;

    // Effect to reset time when selectedDate changes
    useEffect(() => {
        if (selectedDate) { // Only trigger if selectedDate is valid
            if (onChange && typeof onChange === 'function') {
                onChange(null);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate]); // onChange is dependency, ensure it's stable from parent

    // Effect to validate and correct time if selectedDate is today and value exists or changes
    useEffect(() => {
        if (value && selectedDate && moment(selectedDate).isSame(moment(), 'day')) {
            const [hStr, mStr, pStr] = value.split(':');

            if (!hStr || !mStr || !pStr) {
                return;
            }

            let selectedHour24 = parseInt(hStr, 10);
            if (pStr === 'PM' && selectedHour24 !== 12) {
                selectedHour24 += 12;
            } else if (pStr === 'AM' && selectedHour24 === 12) { // 12 AM is hour 0
                selectedHour24 = 0;
            }

            const now = defaultOpenValue ? defaultOpenValue : moment();            
            const currentActualHour = now.hour();
            const currentActualMinute = now.minute();

            const selectedMoment = moment(selectedDate).hour(selectedHour24).minute(parseInt(mStr, 10));

            if (selectedMoment.isBefore(now, 'minute')) {
                const nextValidMinuteRaw = Math.ceil((currentActualMinute + 1) / 5) * 5;
                let newHour24 = currentActualHour;
                let newMinuteValue;

                if (nextValidMinuteRaw >= 60) {
                    newMinuteValue = 0;
                    newHour24 = (currentActualHour + 1);
                    if (newHour24 >= 24) {
                        if (onChange && typeof onChange === 'function') {
                            onChange(null);
                        }
                        return;
                    }
                } else {
                    newMinuteValue = nextValidMinuteRaw;
                }

                const finalNewPeriod = newHour24 >= 12 && newHour24 < 24 ? 'PM' : 'AM';
                let finalNewHour12 = newHour24 % 12;
                if (finalNewHour12 === 0) finalNewHour12 = 12;

                if (onChange && typeof onChange === 'function') {
                    onChange(`${finalNewHour12}:${newMinuteValue.toString().padStart(2, '0')}:${finalNewPeriod}`);
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, selectedDate]); // onChange is dependency, ensure it's stable


    const generateTimeOptions = () => {
        const hours = Array.from({ length: 12 }, (_, i) => i + 1);
        const minutes = Array.from({ length: 12 }, (_, i) => i * 5);
        const periods = ['AM', 'PM'];
        
        return { hours, minutes, periods };
    };

    const getDisabledHours = (currentSelectedPeriodParam) => {
        if (externallyDisabledHours) return generateTimeOptions().hours;
        if (!selectedDate) return [];
        const isToday = moment(selectedDate).isSame(moment(), 'day');
        
        if (!isToday) return [];
        
        const now = defaultOpenValue ? defaultOpenValue : moment();        
        const disabled = [];
        const hoursOptions = generateTimeOptions().hours;
        const periodToEvaluate = currentSelectedPeriodParam || (isToday ? now.format('A') : 'AM');

        if (periodToEvaluate === 'AM' && now.hour() >= 12) {
            return hoursOptions;
        }

        for (const h of hoursOptions) {
            let h24;
            if (periodToEvaluate === 'AM') {
                h24 = (h === 12) ? 0 : h;
            } else {
                h24 = (h === 12) ? 12 : h + 12;
            }
            const endOfHourMoment = moment(selectedDate).hour(h24).minute(59).second(59);
            if (endOfHourMoment.isBefore(now)) {
                disabled.push(h);
            }
        }
        return disabled;
    };

    const getDisabledMinutes = (currentSelectedHourStr, currentSelectedPeriodParam) => {
        if (externallyDisabledMinutes) return generateTimeOptions().minutes;
        if (!selectedDate || !currentSelectedHourStr || !currentSelectedPeriodParam) return [];
        const isToday = moment(selectedDate).isSame(moment(), 'day');
        if (!isToday) return [];

        const now = defaultOpenValue ? defaultOpenValue : moment();
        const disabled = [];
        const minutesOptions = generateTimeOptions().minutes;
        const currentSelectedHourNum = parseInt(currentSelectedHourStr, 10);

        let selectedHour24;
        if (currentSelectedPeriodParam === 'AM') {
            selectedHour24 = (currentSelectedHourNum === 12) ? 0 : currentSelectedHourNum;
        } else {
            selectedHour24 = (currentSelectedHourNum === 12) ? 12 : currentSelectedHourNum + 12;
        }

        if (selectedHour24 === now.hour()) {
            const currentMinuteVal = now.minute();
            minutesOptions.forEach(m => {
                if (m < currentMinuteVal) {
                    disabled.push(m);
                }
            });
        } else if (selectedHour24 < now.hour()) {
            return minutesOptions;
        }
        return disabled;
    };

    const getDisabledPeriods = () => {
        if (!selectedDate) return [];
        const isToday = moment(selectedDate).isSame(moment(), 'day');
        if (!isToday) return [];
        const now = defaultOpenValue ? defaultOpenValue : moment();
        const disabled = [];
        if (now.hour() >= 12) {
            disabled.push('AM');
        }
        return disabled;
    };

    const onHourChange = (newHourStr) => {
        if (!onChange || typeof onChange !== 'function') return;
        const currentMinuteStr = minute || '00';
        let newPeriodStr = period;
        if (!newPeriodStr) {
            const now = moment();
            const isToday = selectedDate && moment(selectedDate).isSame(now, 'day');
            newPeriodStr = isToday ? now.format('A') : 'AM';
        }
        onChange(`${newHourStr}:${currentMinuteStr}:${newPeriodStr}`);
    };

    const onMinuteChange = (newMinuteStr) => {
        if (!onChange || typeof onChange !== 'function') return;
        let currentHourStr = hour;
        let currentPeriodStr = period;
        const now = moment();
        const isToday = selectedDate && moment(selectedDate).isSame(now, 'day');

        if (!currentHourStr) {
            currentHourStr = isToday ? now.format('h') : '12';
        }
        if (!currentPeriodStr) {
            currentPeriodStr = isToday ? now.format('A') : 'AM';
        }
        onChange(`${currentHourStr}:${newMinuteStr}:${currentPeriodStr}`);
    };

    const onPeriodChange = (newPeriodStr) => {
        if (!onChange || typeof onChange !== 'function') return;
        let currentHourStr = hour;
        let currentMinuteStr = minute;
        const now = moment();
        const isToday = selectedDate && moment(selectedDate).isSame(now, 'day');

        if (!currentHourStr) {
            currentHourStr = isToday ? now.format('h') : '12';
        }
        if (!currentMinuteStr) {
            currentMinuteStr = '00';
        }
        onChange(`${currentHourStr}:${currentMinuteStr}:${newPeriodStr}`);
    };

    const disabledHoursList = useMemo(() => getDisabledHours(period), [period, selectedDate, externallyDisabledHours]);
    const disabledMinutesList = useMemo(() => getDisabledMinutes(hour, period), [hour, period, selectedDate, externallyDisabledMinutes]);
    const disabledPeriodsList = useMemo(() => getDisabledPeriods(), [selectedDate]);

    const isInvalidTime = useMemo(() => {
        if (!value || !selectedDate || !moment(selectedDate).isSame(moment(), 'day')) return false;
        const [hStr, mStr, pStr] = value.split(':');
        if (!hStr || !mStr || !pStr) return false;

        const hNum = parseInt(hStr, 10);
        const mNum = parseInt(mStr, 10);

        if (disabledHoursList.includes(hNum)) return true;
        if (disabledMinutesList.includes(mNum)) return true;
        if (disabledPeriodsList.includes(pStr)) return true;

        return false;
    }, [value, selectedDate, disabledHoursList, disabledMinutesList, disabledPeriodsList]);

    const currentHourForSelect = isInvalidTime && !externallyDisabledHours ? undefined : hour;
    const currentMinuteForSelect = isInvalidTime && !externallyDisabledMinutes ? undefined : minute;
    const currentPeriodForSelect = isInvalidTime ? undefined : period;

    return (
        <div style={{
            display: 'flex',
            gap: isMobile ? '8px' : '10px',
            alignItems: 'center',
            width: '100%',
            flexDirection: isMobile ? 'column' : 'row',
            ...style
        }}>
            <Select
                style={{
                    flex: isMobile ? 'none' : isTablet ? '1.2' : 1,
                    width: isMobile ? '100%' : 'auto',
                    minWidth: isMobile ? '100%' : '80px',
                    marginBottom: isMobile ? '8px' : 0
                }}
                placeholder={hourLabel}
                value={currentHourForSelect}
                onChange={onHourChange}
                disabled={externallyDisabledHours}
                status={isInvalidTime && !externallyDisabledHours ? 'error' : ''}
                defaultValue={defaultOpenValue ? moment(defaultOpenValue).hour() : undefined}
            >
                {generateTimeOptions().hours.map(hVal => (
                    <Option key={hVal} value={hVal.toString()} disabled={disabledHoursList.includes(hVal)}>
                        {hVal}
                    </Option>
                ))}
            </Select>
            <Select
                style={{
                    flex: isMobile ? 'none' : isTablet ? '1.2' : 1,
                    width: isMobile ? '100%' : 'auto',
                    minWidth: isMobile ? '100%' : '80px',
                    marginBottom: isMobile ? '8px' : 0
                }}
                placeholder={minuteLabel}
                value={currentMinuteForSelect}
                onChange={onMinuteChange}
                disabled={externallyDisabledMinutes || !hour || (hour && disabledHoursList.includes(parseInt(hour,10)))}
                status={isInvalidTime && !externallyDisabledMinutes ? 'error' : ''}
            >
                {generateTimeOptions().minutes.map(mVal => (
                    <Option key={mVal} value={mVal.toString().padStart(2, '0')} disabled={disabledMinutesList.includes(mVal)}>
                        {mVal.toString().padStart(2, '0')}
                    </Option>
                ))}
            </Select>
            <Select
                style={{
                    flex: isMobile ? 'none' : isTablet ? '1' : 1,
                    width: isMobile ? '100%' : 'auto',
                    minWidth: isMobile ? '100%' : '80px'
                }}
                placeholder={ampmLabel}
                value={currentPeriodForSelect}
                onChange={onPeriodChange}
                disabled={!hour || !minute || (hour && disabledHoursList.includes(parseInt(hour,10))) || (minute && disabledMinutesList.includes(parseInt(minute,10)))}
                status={isInvalidTime ? 'error' : ''}
            >
                {generateTimeOptions().periods.map(pVal => (
                    <Option key={pVal} value={pVal} disabled={disabledPeriodsList.includes(pVal)}>
                        {pVal}
                    </Option>
                ))}
            </Select>
        </div>
    );
};

CustomTimePicker.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.oneOf([null])
    ]),
    onChange: PropTypes.func.isRequired, // Mark as required, ensure it's provided
    disabledHours: PropTypes.bool,
    disabledMinutes: PropTypes.bool,
    hourLabel: PropTypes.string,
    minuteLabel: PropTypes.string,
    ampmLabel: PropTypes.string,
    style: PropTypes.object,
    selectedDate: PropTypes.string,
    defaultOpenValue: PropTypes.string
};

CustomTimePicker.defaultProps = {
    value: null,
    disabledHours: false,
    disabledMinutes: false,
    hourLabel: 'Hour',
    minuteLabel: 'Minute',
    ampmLabel: 'AM/PM',
    style: {},
    selectedDate: null,
    defaultOpenValue: null
};

export default CustomTimePicker;